/**
 * GarmentFlat — thin React wrapper over the domain's flat drawing grammar
 * (`@flint/domain/garment/designs/flat-svg`), so the studio canvas, the
 * tech-pack screen, and the server-rendered PDF all draw from ONE source.
 * The module is dependency-free geometry — the SPA bundles no domain infra.
 *
 * Phase 4a adds interactive part selection via `data-part` attribute groups.
 */
import { useMemo } from "react";
import { garmentFlatSvg } from "@flint/domain/garment/designs/flat-svg";

/** A CSS `fill` value is only interpolated into the injected <style> if it's a
 *  plain hex colour. Colourway hex comes from tenant data, so a malformed value
 *  ("#fff }") could otherwise break the wash or inject arbitrary rules. */
const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Maps the app's theme tokens onto the drawing grammar's `--flat-*` vars
 * (the domain strings fall back to light-theme literals for the PDF).
 */
export const FLAT_THEME_VARS = {
  "--flat-outline": "var(--slate-700)",
  "--flat-stitch": "var(--line-2)",
  "--flat-fill": "var(--paper)",
  "--flat-ink": "var(--ink)",
} as React.CSSProperties;

export function GarmentFlat({
  garmentType,
  view,
  viewBox = "0 0 400 500",
  onPartClick,
  selectedPart,
  partColours,
}: {
  garmentType: string;
  view: "front" | "back";
  /** Override the SVG viewBox. The design studio must keep the default
   *  `"0 0 400 500"` so element placement coordinates stay aligned.
   *  Preview-only surfaces can pass a tighter crop to fill the frame. */
  viewBox?: string;
  /** Called when a `data-part` group is clicked. Empty string = deselect. */
  onPartClick?: ((part: string) => void) | undefined;
  /** The currently-selected part name. Non-selected parts dim to 30%. */
  selectedPart?: string | undefined;
  /** Part name → hex colour. Tagged parts get a translucent fill wash. */
  partColours?: Record<string, string> | undefined;
}) {
  // The flat geometry is a large string; rebuild it only when the drawing
  // actually changes, not on every pin drag / selection / tint tweak.
  const svgContent = useMemo(
    () => garmentFlatSvg(garmentType, view),
    [garmentType, view],
  );

  const finalSvg = useMemo(() => {
    // Inject colour tint CSS for parts with assigned colours. Only well-formed
    // hex values are interpolated (see HEX_COLOUR).
    // Untagged garment types (no `data-part` groups) → no selectors match → no-op.
    const colourStyles = partColours
      ? Object.entries(partColours)
          .filter(([, hex]) => HEX_COLOUR.test(hex))
          .map(
            ([part, hex]) =>
              `[data-part="${part}"] path { fill: ${hex}; fill-opacity: 0.35; }`,
          )
          .join("\n")
      : "";

    // Inject highlight CSS when part selection is active.
    const highlightStyle = selectedPart
      ? `<style>
      [data-part] { cursor: pointer; }
      [data-part]:not([data-part="${selectedPart}"]) { opacity: 0.3; transition: opacity 0.15s; }
      [data-part="${selectedPart}"] path { stroke: var(--sky-600); stroke-width: 3; }
      ${colourStyles}
    </style>`
      : onPartClick
        ? `<style>[data-part] { cursor: pointer; } ${colourStyles}</style>`
        : colourStyles
          ? `<style>${colourStyles}</style>`
          : "";

    return highlightStyle + svgContent;
  }, [svgContent, partColours, selectedPart, onPartClick]);

  function handleClick(e: React.MouseEvent<SVGSVGElement>) {
    if (!onPartClick) return;
    const target = (e.target as Element).closest("[data-part]");
    onPartClick(target ? target.getAttribute("data-part") ?? "" : "");
  }

  return (
    <svg
      viewBox={viewBox}
      className="h-full w-full"
      style={FLAT_THEME_VARS}
      aria-hidden
      onClick={handleClick}
      // Static geometry from our own domain module; the only interpolated
      // values are the SVG's own data-part names and hex-validated colours.
      dangerouslySetInnerHTML={{ __html: finalSvg }}
    />
  );
}
